import { useState, useRef, useEffect, useCallback } from 'react'
import { AlertCircle, Bot, CheckCircle2, SendHorizonal, Square, Wand2 } from 'lucide-react'
import {
  Button,
  ChatBubble,
  Modal,
} from '@/components/ui'

// ─── Types ────────────────────────────────────────────────────────────────────

interface ChatMessage {
  role: 'user' | 'assistant' | 'error'
  content: string
}

interface ToolEvent {
  name: string
  input: Record<string, unknown>
  output?: unknown
}

interface CreatedArticle {
  id: string
  itemCode: string
  itemName: string
  groupName?: string
  salesPrice: number
  purchasePrice: number
  unit: string
}

type StreamEvent =
  | { type: 'connected' }
  | { type: 'text-delta'; text: string }
  | { type: 'tool-start'; name: string; input: Record<string, unknown> }
  | { type: 'tool-result'; name: string; output: unknown }
  | { type: 'article-created'; article: CreatedArticle }
  | { type: 'done' }
  | { type: 'error'; message: string }

// ─── SSE helper ───────────────────────────────────────────────────────────────

async function streamArticleChat(
  history: { role: 'user' | 'assistant'; content: string }[],
  onEvent: (e: StreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  }
  const authState = localStorage.getItem('auth-storage')
  if (authState) {
    try {
      const parsed = JSON.parse(authState) as { state?: { accessToken?: string } }
      const token = parsed.state?.accessToken
      if (token) headers['Authorization'] = `Bearer ${token}`
    } catch { /* ignore */ }
  }
  const tenantStored = localStorage.getItem('tenant-storage')
  if (tenantStored) {
    try {
      const parsed = JSON.parse(tenantStored) as { state?: { activeTenantId?: string } }
      const tenantId = parsed.state?.activeTenantId
      if (tenantId) headers['X-Tenant-Id'] = tenantId
    } catch { /* ignore */ }
  }

  const res = await fetch('/api/articles/ai/chat', {
    method: 'POST',
    headers,
    body: JSON.stringify({ history }),
    signal,
  })

  if (!res.ok || !res.body) {
    let detail = ''
    try {
      const j = await res.json()
      detail = (j as { error?: string }).error ?? ''
    } catch {
      detail = await res.text().catch(() => '')
    }
    throw new Error(detail || `HTTP ${res.status}`)
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    let sepIdx: number
    while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
      const rawEvent = buffer.slice(0, sepIdx)
      buffer = buffer.slice(sepIdx + 2)
      const dataLine = rawEvent.split('\n').find((l) => l.startsWith('data: '))
      if (!dataLine) continue
      const json = dataLine.slice(6).trim()
      if (!json) continue
      try {
        const evt = JSON.parse(json) as StreamEvent
        onEvent(evt)
        if (evt.type === 'done' || evt.type === 'error') return
      } catch { /* malformed event */ }
    }
  }
}

// ─── Modal component ──────────────────────────────────────────────────────────

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  onArticlesCreated?: () => void
  initialText?: string
}

export function ArtikelAgentModal({ open, onOpenChange, onArticlesCreated, initialText }: Props) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [streaming, setStreaming] = useState(false)
  const [currentStreamText, setCurrentStreamText] = useState('')
  const [toolEvents, setToolEvents] = useState<ToolEvent[]>([])
  const [createdArticles, setCreatedArticles] = useState<CreatedArticle[]>([])
  const abortRef = useRef<AbortController | null>(null)
  const streamingRef = useRef(false)
  const bottomRef = useRef<HTMLDivElement>(null)
  const initialSentRef = useRef(false)

  // Auto-scroll to bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, currentStreamText, toolEvents])

  // Pre-fill if initialText provided
  useEffect(() => {
    if (open && initialText && !initialSentRef.current) {
      initialSentRef.current = true
      setInput(initialText)
    }
  }, [open, initialText])

  // Reset on close
  useEffect(() => {
    if (!open) {
      setMessages([])
      setInput('')
      setCurrentStreamText('')
      setToolEvents([])
      setCreatedArticles([])
      setStreaming(false)
      streamingRef.current = false
      initialSentRef.current = false
      abortRef.current?.abort()
    }
  }, [open])

  const send = useCallback(async (text?: string) => {
    const content = (text ?? input).trim()
    if (!content || streamingRef.current) return
    streamingRef.current = true

    const userMsg: ChatMessage = { role: 'user', content }
    // Only pass user/assistant messages to the API (not 'error' role)
    const apiHistory = [...messages, userMsg]
      .filter((m): m is { role: 'user' | 'assistant'; content: string } =>
        m.role === 'user' || m.role === 'assistant',
      )

    setMessages((prev) => [...prev, userMsg])
    setInput('')
    setStreaming(true)

    let assistantText = ''
    let pendingTools: ToolEvent[] = []
    let errorMsg: string | null = null

    // Attempt the request up to twice — retry silently when the backend connects
    // but returns no text and called no tools (e.g. model produced a tool-only turn
    // without accompanying text, which can happen on short confirmations like "Ja").
    for (let attempt = 0; attempt <= 1; attempt++) {
      assistantText = ''
      pendingTools = []
      errorMsg = null

      setCurrentStreamText('')
      setToolEvents([])

      const abort = new AbortController()
      abortRef.current = abort

      let connected = false
      try {
        await streamArticleChat(
          apiHistory,
          (evt) => {
            if (evt.type === 'connected') {
              connected = true
            } else if (evt.type === 'text-delta') {
              assistantText += evt.text
              setCurrentStreamText(assistantText)
            } else if (evt.type === 'tool-start') {
              const toolEvt: ToolEvent = { name: evt.name, input: evt.input }
              pendingTools = [...pendingTools, toolEvt]
              setToolEvents(pendingTools)
            } else if (evt.type === 'tool-result') {
              pendingTools = pendingTools.map((t, i) =>
                i === pendingTools.length - 1 && t.name === evt.name
                  ? { ...t, output: evt.output }
                  : t,
              )
              setToolEvents(pendingTools)
            } else if (evt.type === 'article-created') {
              setCreatedArticles((prev) => [...prev, evt.article])
            } else if (evt.type === 'error') {
              errorMsg = evt.message
            }
          },
          abort.signal,
        )
      } catch (err) {
        if ((err as { name?: string }).name !== 'AbortError') {
          errorMsg = (err as Error).message
        }
      }

      // Got a real response (text or explicit error) — done
      if (assistantText || errorMsg) break

      if (!connected) {
        errorMsg = 'Verbindung zum Server konnte nicht aufgebaut werden. Bitte Seite neu laden.'
        break
      }

      // Connected but empty with tools executed — don't retry (articles may have been created)
      if (pendingTools.length > 0) {
        errorMsg = `Der Assistent hat ${pendingTools.length} Aktion(en) ausgeführt, aber keine Bestätigung produziert. Bitte prüfe ob die Artikel angelegt wurden, und sende deine Nachricht ggf. erneut.`
        break
      }

      // Connected, no text, no tools, no error — retry once, then give up
      if (attempt >= 1) {
        errorMsg = 'Verbindung zum KI-Server unterbrochen — keine Antwort erhalten. Bitte sende deine Nachricht erneut. Falls das Problem anhält, prüfe die KI-Konfiguration unter Einstellungen → Global → KI.'
      }
      // attempt === 0: loop continues for one more try
    }

    // Commit streamed assistant text and clear live indicators
    setMessages((prev) => {
      const next = [...prev]
      if (assistantText) next.push({ role: 'assistant', content: assistantText })
      if (errorMsg) next.push({ role: 'error', content: errorMsg })
      return next
    })
    setCurrentStreamText('')
    setToolEvents([])
    setStreaming(false)
    streamingRef.current = false
    abortRef.current = null
  }, [input, messages])

  function handleStop() {
    abortRef.current?.abort()
  }

  function handleClose() {
    abortRef.current?.abort()
    if (createdArticles.length > 0) {
      onArticlesCreated?.()
    }
    onOpenChange(false)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void send()
    }
  }

  const isEmpty = messages.length === 0 && !streaming

  return (
    <Modal
      open={open}
      onOpenChange={(v) => { if (!v) handleClose() }}
      title="Artikel-Assistent"
      size="full"
    >
      <div className="flex flex-col" style={{ height: 'calc(80vh - 80px)' }}>
        {/* Chat area */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 min-h-0">
          {isEmpty && (
            <div className="flex flex-col items-center justify-center h-full text-center gap-3 text-gray-400">
              <div className="w-12 h-12 rounded-full bg-brand-50 flex items-center justify-center">
                <Wand2 className="w-6 h-6 text-brand-600" />
              </div>
              <div>
                <p className="text-body font-medium text-gray-700">Artikel aus Text erstellen</p>
                <p className="text-small text-gray-500 mt-1 max-w-sm">
                  Beschreibe Produkte, Dienstleistungen oder Preismodelle – der Assistent
                  erkennt die Artikel und legt sie nach Bestätigung an.
                </p>
              </div>
            </div>
          )}

          {/* Rendered messages */}
          {messages.map((msg, i) => {
            if (msg.role === 'error') {
              return (
                <div key={i} className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-red-100 border border-red-200 flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="w-4 h-4 text-red-600" />
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-2xl rounded-tl-none px-4 py-3 text-body text-red-800 max-w-[75%]">
                    <p className="font-medium text-small mb-0.5">Fehler</p>
                    <p className="text-small">{msg.content}</p>
                  </div>
                </div>
              )
            }
            return (
              <ChatBubble
                key={i}
                role={msg.role}
                content={msg.content}
              />
            )
          })}

          {/* Tool events during active stream */}
          {toolEvents.map((t, i) => (
            <ChatBubble
              key={`tool-${i}`}
              role="tool"
              content=""
              toolName={t.name}
              toolInput={t.input}
              toolOutput={t.output}
            />
          ))}

          {/* Streaming assistant text */}
          {currentStreamText && (
            <ChatBubble role="assistant" content={currentStreamText} />
          )}

          {/* Typing indicator */}
          {streaming && !currentStreamText && toolEvents.length === 0 && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0">
                <Bot className="w-4 h-4 text-brand-700" />
              </div>
              <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-none px-4 py-3">
                <span className="inline-flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                </span>
              </div>
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* Created articles panel */}
        {createdArticles.length > 0 && (
          <div className="border-t border-green-200 bg-green-50 px-4 py-3 flex-shrink-0">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              <span className="text-small font-semibold text-green-800">
                {createdArticles.length} Artikel angelegt
              </span>
            </div>
            <div className="space-y-1">
              {createdArticles.map((a) => (
                <div key={a.id} className="flex items-center justify-between text-caption text-green-900">
                  <span className="font-mono font-medium flex-shrink-0">{a.itemCode}</span>
                  <span className="truncate mx-2 text-green-700">{a.itemName}</span>
                  <span className="text-green-600 flex-shrink-0">
                    {a.salesPrice > 0 ? `${a.salesPrice.toFixed(2)} €` : '–'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Input area */}
        <div className="border-t border-gray-200 px-4 py-3 flex gap-2 items-end flex-shrink-0">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Beschreibung, Preisliste oder Anfrage eingeben…"
            className="flex-1 min-h-[44px] max-h-48 resize-none rounded-xl border border-gray-300 px-3 py-2.5 text-body placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent leading-snug"
            rows={2}
            disabled={streaming}
          />
          {streaming ? (
            <Button
              variant="danger"
              size="md"
              onClick={handleStop}
              className="flex-shrink-0 self-end"
            >
              <Square className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              variant="primary"
              size="md"
              onClick={() => void send()}
              disabled={!input.trim()}
              className="flex-shrink-0 self-end"
            >
              <SendHorizonal className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}
